import { CommonBaseService } from './../../services/common-base.service';
import { ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, DestroyRef, inject, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { appInjector as appInjector } from '@app/shared/classes/app-injector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { JsonLdService } from '@app/core/services/json-ld.service';


@Component({
    selector: 'app-base',
    templateUrl: './base.component.html',
    styleUrls: ['./base.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class BaseComponent extends CommonBaseService implements OnInit, AfterViewInit, OnDestroy {

    public viewportScroller: ViewportScroller
    public router: Router;
    public route: ActivatedRoute;
    protected titleService: Title;
    protected translate: TranslateService;
    protected jsonLdService: JsonLdService;
    protected readonly destroyRef = inject(DestroyRef);

    // Child components MUST set this property
    protected abstract pageTitle: string;

    constructor() {
        super();
        this.viewportScroller = appInjector.get(ViewportScroller);
        this.router = appInjector.get(Router);
        this.route = appInjector.get(ActivatedRoute);
        this.titleService = appInjector.get(Title);
        this.translate = appInjector.get(TranslateService);
        this.jsonLdService = appInjector.get(JsonLdService);
    }

    ngAfterViewInit(): void {
        this.router.events
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(val => {
            if (val instanceof NavigationEnd) {
                let fragmentIdx = val.urlAfterRedirects.lastIndexOf('#');
                if (fragmentIdx >= 0 && fragmentIdx < val.urlAfterRedirects.length - 1) {
                    let fragment = val.urlAfterRedirects.substring(fragmentIdx + 1);
                    this.log.debug('fragment: ' + fragment);
                    setTimeout(() => {
                        document.getElementById(fragment).scrollIntoView();
                    }, 1500);

                }
            }
        });

        // Set page title after view init to avoid race conditions
        this.setPageTitle();
    }

    ngOnInit(): void {
        this.log.trace(`BaseComponent created with id: ${this.id}`);
    }

    /**
     * Sets the page title with translation support
     * Preserves unread notification count prefix if present
     */
    protected setPageTitle(): void {
        this.translate.stream(this.pageTitle)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((translatedTitle: string) => {
            const title = translatedTitle !== this.pageTitle ? translatedTitle : this.pageTitle;

            // Check if current title has unread count prefix "(123) "
            const currentTitle = this.titleService.getTitle();
            const unreadMatch = currentTitle.match(/^\((\d+)\)\s+/);
            const prefix = unreadMatch ? `(${unreadMatch[1]}) ` : '';

            this.titleService.setTitle(`${prefix}${title} - Navizard`);
            this.jsonLdService.scheduleUpdate(this.router.url);
        });
    }

    public imageErrorHandler(event: any): void {
        event.target.src = './assets/images/noimage.png';
    }

    ngOnDestroy(): void {
        this.log.trace(`BaseComponent destroy with id: ${this.id}`);
    }

}
